/**
 * Theme wishlist — localStorage-backed favorites.
 * Storage key: theme:wishlist
 * Item shape: { id, handle, url, title, image, price, vendor, variantId }
 */
const STORAGE_KEY = 'theme:wishlist';
const CHANGE_EVENT = 'theme:wishlist:change';

function readItems() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeItems(items) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  window.dispatchEvent(
    new CustomEvent(CHANGE_EVENT, {
      detail: { items, count: items.length },
    })
  );
}

export function getWishlistItems() {
  return readItems();
}

export function getWishlistCount() {
  return readItems().length;
}

export function isInWishlist(productId) {
  const id = String(productId);
  return readItems().some((item) => String(item.id) === id);
}

export function addToWishlist(item) {
  if (!item?.id) return false;
  const items = readItems();
  if (items.some((entry) => String(entry.id) === String(item.id))) return false;
  items.unshift({
    id: String(item.id),
    handle: item.handle || '',
    url: item.url || '',
    title: item.title || '',
    image: item.image || '',
    price: item.price || '',
    vendor: item.vendor || '',
    variantId: item.variantId ? String(item.variantId) : '',
  });
  writeItems(items);
  return true;
}

export function removeFromWishlist(productId) {
  const id = String(productId);
  const next = readItems().filter((item) => String(item.id) !== id);
  writeItems(next);
  return true;
}

export function toggleWishlist(item) {
  if (!item?.id) return false;
  if (isInWishlist(item.id)) {
    removeFromWishlist(item.id);
    return false;
  }
  addToWishlist(item);
  return true;
}

function syncButtons(root = document) {
  const items = readItems();
  const ids = new Set(items.map((item) => String(item.id)));

  root.querySelectorAll('[data-wishlist-toggle]').forEach((button) => {
    const productId = String(button.dataset.productId || '');
    const active = ids.has(productId);
    button.classList.toggle('is-active', active);
    button.setAttribute('aria-pressed', active ? 'true' : 'false');

    const labelAdd = button.dataset.labelAdd || 'Add to Wish List';
    const labelRemove = button.dataset.labelRemove || 'Remove from Wish List';
    const labelEl = button.querySelector('[data-wishlist-label]');
    if (labelEl) labelEl.textContent = active ? labelRemove : labelAdd;
    button.setAttribute('aria-label', active ? labelRemove : labelAdd);
  });

  root.querySelectorAll('[data-wishlist-count]').forEach((el) => {
    const count = items.length;
    el.textContent = count > 0 ? String(count) : '';
    el.hidden = count === 0;
    el.closest('[data-wishlist-count-wrap]')?.classList.toggle('has-items', count > 0);
  });
}

function bindToggles(root = document) {
  root.querySelectorAll('[data-wishlist-toggle]').forEach((button) => {
    if (button.dataset.wishlistBound === 'true') return;
    button.dataset.wishlistBound = 'true';

    button.addEventListener('click', (event) => {
      event.preventDefault();
      const item = {
        id: button.dataset.productId,
        handle: button.dataset.productHandle,
        url: button.dataset.productUrl,
        title: button.dataset.productTitle,
        image: button.dataset.productImage,
        price: button.dataset.productPrice,
        vendor: button.dataset.productVendor,
        variantId: button.dataset.variantId,
      };
      toggleWishlist(item);
    });
  });
}

function renderPage(root) {
  if (!root) return;
  const emptyEl = root.querySelector('[data-wishlist-empty]');
  const gridEl = root.querySelector('[data-wishlist-grid]');
  const template = root.querySelector('[data-wishlist-card-template]');
  if (!gridEl || !template) return;

  const items = readItems();
  gridEl.innerHTML = '';

  if (items.length === 0) {
    emptyEl?.removeAttribute('hidden');
    gridEl.setAttribute('hidden', '');
    return;
  }

  emptyEl?.setAttribute('hidden', '');
  gridEl.removeAttribute('hidden');

  items.forEach((item) => {
    const node = template.content.cloneNode(true);
    const card = node.querySelector('[data-wishlist-card]');
    const link = node.querySelector('[data-wishlist-link]');
    const img = node.querySelector('[data-wishlist-image]');
    const title = node.querySelector('[data-wishlist-title]');
    const vendor = node.querySelector('[data-wishlist-vendor]');
    const price = node.querySelector('[data-wishlist-price]');
    const removeBtn = node.querySelector('[data-wishlist-remove]');

    if (card) card.dataset.productId = item.id;
    if (link) {
      link.href = item.url || '#';
      link.setAttribute('aria-label', item.title || 'Product');
    }
    if (img) {
      if (item.image) {
        img.src = item.image;
        img.alt = item.title || '';
        img.removeAttribute('hidden');
      } else {
        img.setAttribute('hidden', '');
      }
    }
    if (title) title.textContent = item.title || '';
    if (vendor) {
      if (item.vendor) {
        vendor.textContent = item.vendor;
        vendor.removeAttribute('hidden');
      } else {
        vendor.setAttribute('hidden', '');
      }
    }
    if (price) {
      if (item.price) {
        price.textContent = item.price;
        price.removeAttribute('hidden');
      } else {
        price.setAttribute('hidden', '');
      }
    }
    if (removeBtn) {
      removeBtn.addEventListener('click', () => removeFromWishlist(item.id));
    }

    gridEl.appendChild(node);
  });
}

function initWishlist() {
  bindToggles(document);
  syncButtons(document);
  document.querySelectorAll('[data-wishlist-page]').forEach((root) => renderPage(root));
}

window.addEventListener(CHANGE_EVENT, () => {
  syncButtons(document);
  document.querySelectorAll('[data-wishlist-page]').forEach((root) => renderPage(root));
});

document.addEventListener('shopify:section:load', initWishlist);

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initWishlist);
} else {
  initWishlist();
}
